"use client";

import { PieceIcon, type PieceType } from "@/components/pieces/piece-icon";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/shadcn/alert-dialog";
import { Button } from "@/components/shadcn/button";
import { Card } from "@/components/shadcn/card";
import { useLocalizeHref, usePathname, useRouter } from "@/i18n/navigation";
import { trackBeginCheckout } from "@/lib/analytics";
import { useSession } from "@/lib/auth/client";
import { getLoginHref } from "@/lib/auth/login-redirect";
import useStore from "@/store/useStore";
import { Check } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

type PremiumPlanCardProps = {
    id: number;
    title: string;
    summary: string;
    description: string;
    price: number;
    originalPrice: number | null;
    duration: number;
    subscription: boolean;
    iyzicoRefKey: string | null;
    activeProductId: number | null;
    piece?: PieceType;
};

export default function PremiumPlanCard({
    id,
    title,
    summary,
    description,
    price,
    originalPrice,
    duration,
    subscription,
    iyzicoRefKey,
    activeProductId,
    piece = "pawn",
}: PremiumPlanCardProps) {
    const t = useTranslations("premium");
    const isCurrent = activeProductId === id;
    const router = useRouter();
    const pathname = usePathname();
    const localizeHref = useLocalizeHref();
    // The pid lets the checkout page recover the product even when the
    // store's localStorage copy doesn't survive the full-page navigation
    // (TTL expiry, rehydration races) — bouncing back was the old failure.
    const checkoutHref = localizeHref(`/premium/checkout?pid=${id}`);
    const { data: session, isPending: isSessionPending } = useSession();
    const setProduct = useStore((state) => state.setProduct);
    const [alertOpen, setAlertOpen] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");

    const formatNumber = (price: number) => {
        return new Intl.NumberFormat("tr-TR", {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(price);
    };

    const productBasketType: { [key: string]: string } = {
        1: "subscriptionOrder",
        2: "basketOrder",
        3: "basketOrder",
        4: "basketOrder",
        5: "donationOrder",
        6: "giftOrder",
    };

    const handleSelectPlan = () => {
        // The cards are server-rendered and clickable before the client
        // session resolves; deciding "not logged in" from a pending session
        // would bounce a signed-in user to /login.
        if (isSessionPending) return;

        trackBeginCheckout({ id, title, price: Number(price) || 0 });

        // Set product in Zustand store
        setProduct({
            Id: id,
            Title: title,
            Price: price,
            Amount: 1,
            Duration: duration,
            Type:
                productBasketType[id.toString()] ||
                (subscription ? "subscriptionOrder" : "basketOrder"),
            pricingRefCode: iyzicoRefKey || "",
            gifted_username: "",
            gift_message: "",
            gifted_user_id: "",
            gifted_usermail: "",
        });

        // Check if user is logged in
        if (!session) {
            router.push(getLoginHref(pathname || "/premium"));
            return;
        }

        const basketOrderIds = [2, 3, 4];

        // Allow checkout if no active purchase
        if (activeProductId === null) {
            //router.push("/premium/checkout");
            window.location.assign(checkoutHref);
            return;
        }

        // Handle basket order scenarios
        if (basketOrderIds.includes(activeProductId)) {
            if (id !== 1) {
                // Buying another basket order → allow
                //router.push("/premium/checkout");
                window.location.assign(checkoutHref);
                return;
            } else {
                // Trying to buy subscription while having basket order → block
                setAlertMessage(t("planActivePurchase"));
                setAlertOpen(true);
                return;
            }
        }

        // Handle subscription scenarios
        if (activeProductId === 1) {
            if (id === 1) {
                // Trying to buy another subscription → block
                setAlertMessage(t("planHasMonthly"));
                setAlertOpen(true);
                return;
            } else if (basketOrderIds.includes(id)) {
                // Buying basket order while having subscription → warn and allow
                setAlertMessage(t("planWillCancel"));
                setAlertOpen(true);
                return;
            }
        }

        // Default: allow checkout
        //router.push("/premium/checkout");
        window.location.assign(checkoutHref);
    };

    const handleAlertContinue = () => {
        setAlertOpen(false);
        const basketOrderIds = [2, 3, 4];

        // If has subscription and buying basket order → allow (will cancel subscription)
        if (activeProductId === 1 && basketOrderIds.includes(id)) {
            //router.push("/premium/checkout");
            window.location.assign(checkoutHref);
        }
    };

    return (
        <>
            <Card className="flex h-full flex-col justify-between gap-2 rounded-xl p-4">
                {/* Header */}
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1">
                        <PieceIcon
                            piece={piece}
                            className="text-primary h-5 w-5 sm:h-6 sm:w-6"
                        />
                        <h1 className="text-sm font-[500] sm:text-base">
                            {title}
                        </h1>
                    </div>
                    {isCurrent && (
                        <span className="text-primary bg-primary/10 rounded-lg px-2 py-1 text-[10px] font-semibold sm:px-3 sm:py-1.5 sm:text-xs">
                            MEVCUT PLANIN
                        </span>
                    )}
                </div>

                {/* Badge */}
                {summary && (
                    <div className="bg-success-foreground text-success inline-flex items-center gap-2 self-start rounded-lg px-3 py-1.5 text-xs font-semibold">
                        <Check
                            className="bg-success text-success-foreground h-3 w-3 rounded-full p-0.5 font-bold"
                            strokeWidth={4}
                        />
                        <span>{summary}</span>
                    </div>
                )}

                {/* Description */}
                {description && (
                    <p className="text-muted-foreground flex-1 text-xs">
                        {description}
                    </p>
                )}

                {/* Price */}
                <div className="flex items-baseline">
                    <span className="text-foreground text-xl font-bold sm:text-2xl">
                        ₺{formatNumber(price)}
                    </span>
                    {originalPrice && (
                        <p className="text-muted-foreground mt-1 ml-1.5 self-start text-xs sm:text-sm">
                            <s>₺{formatNumber(originalPrice)}</s>
                        </p>
                    )}
                </div>

                {/* CTA Button */}
                <Button
                    onClick={handleSelectPlan}
                    className="rounded-lg"
                    // Basket packages (2/3/4) can be re-bought to extend their
                    // duration (a new purchase row stacks via setStartDate).
                    // Only the recurring subscription (ProductId 1) stays
                    // disabled on its current plan.
                    disabled={isCurrent && id === 1}
                    variant={isCurrent && id === 1 ? "secondary" : "default"}
                    data-gtm-id={`premium-plan-btn-${id}`}
                >
                    {activeProductId === null
                        ? t("planGoPremium")
                        : isCurrent
                          ? id === 1
                              ? t("planCurrent")
                              : t("planExtend")
                          : t("planSwitch")}
                </Button>
            </Card>

            {/* Alert Dialog */}
            <AlertDialog open={alertOpen} onOpenChange={setAlertOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Bilgi</AlertDialogTitle>
                        <AlertDialogDescription>
                            {alertMessage}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t("cancel")}</AlertDialogCancel>
                        <AlertDialogAction onClick={handleAlertContinue}>
                            Tamam
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
